import { graphql, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import React, { useEffect, useState, useRef } from 'react';
import { useLayoutContext } from '../providers/LayoutProvider';
import { Footer } from './Footer/Footer';
import { Header } from './Header/Header';
import { PromoBarInputs } from './Header/PromoBar/PromoBar';
import { useScrollToAnchor } from '../shared/useScrollToAnchor';
import './Layout.scss';
import { SiteSidebar } from './SiteSidebar/SiteSidebar';
import { CampaignTracking } from '../shared/Utils';

export default function Layout({
    isHomepage,
    promoBar,
    children,
}: {
    isHomepage?: boolean;
    promoBar?: PromoBarInputs;
    children: React.ReactNode;
}) {
    const {
        allSitePage: { nodes: pages },
        allTaxonomyTermCampaignTerms: { nodes: campaignTerms },
    } = useStaticQuery(graphql`
        query {
            allSitePage {
                nodes {
                    pageContext
                    path
                }
            }
            allTaxonomyTermCampaignTerms {
                nodes {
                    name
                }
            }
        }
    `);
    const {
        sidebarExpanded,
        inlineAnchor,
        setInlineAnchor,
        setNodeLookup,
        setCampaignTermsFilter,
    } = useLayoutContext();
    const windowLocation = useLocation();
    const [scrolled, setIsScrolled] = useState(false);
    const [jumpToMain, setJumpToMain] = useState(false);
    const [excludeSearchBool, setExcludeSearchBool] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const mainRef = useRef<HTMLElement>(null);
    const [anchorPos, setAnchorPos] = useState({
        anchorScrollTop: null,
        anchorRef: null,
    });
    const campaignTermsQuery = campaignTerms?.flatMap((term) => {
        if (term?.name) {
            return [term.name];
        }
        return [];
    });

    useEffect(() => {
        const lookup = pages
            .filter((page) => page.pageContext[`drupal_internal__nid`])
            .map((page) => ({
                nodeId: page.pageContext[`drupal_internal__nid`],
                path: page.path,
            }));
        setNodeLookup(lookup);

        if (typeof window !== `undefined`) {
            const urlLocation = window.location;
            if (urlLocation.toString().indexOf(`/search`) !== -1) {
                setExcludeSearchBool(true);
            } else {
                setExcludeSearchBool(false);
            }
            setCampaignTermsFilter(campaignTermsQuery);
            CampaignTracking(windowLocation);
        }
    }, []);

    useEffect(() => {
        if (jumpToMain) {
            scrollRef.current?.scrollTo({
                top: 0,
                left: 0,
                behavior: `auto`,
            });
            setJumpToMain(false);
        }
    }, [jumpToMain]);

    const handleJumpToMain = (
        evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        evt.preventDefault();
        setJumpToMain(true);
        evt.currentTarget.blur();
        mainRef.current.focus();
        if (typeof window !== `undefined`) {
            history.pushState(
                null,
                ``,
                window.location.pathname + `#maincontent`
            );
        }
    };

    useEffect(() => {
        //inlineAnchor passed from Article Source list buttons
        if (!inlineAnchor) return;
        useScrollToAnchor(
            inlineAnchor,
            setInlineAnchor,
            anchorPos,
            setAnchorPos,
            scrollRef
        );
    }, [inlineAnchor, setAnchorPos, anchorPos, scrollRef]);

    const handleClickedEl = (
        evt: React.MouseEvent<HTMLElement>,
        clickElPos?: number
    ) => {
        const targetTag: HTMLAnchorElement = evt.target as HTMLAnchorElement;
        const targetTagHref = targetTag.closest(`a`);

        if (targetTagHref) {
            setAnchorPos({
                anchorScrollTop: clickElPos,
                anchorRef: targetTagHref,
            });
        }
    };

    return (
        <>
            <a
                id="skipToMain"
                className="sr-only"
                href="#maincontent"
                onClick={handleJumpToMain}
            >
                Skip to main content
            </a>
            <div
                ref={scrollRef}
                className={`layout light _vwo_scroll_fix ${
                    sidebarExpanded ? `static` : ``
                }`}
                onScroll={(evt) => {
                    const elScrollTop = (evt?.target as HTMLDivElement)
                        ?.scrollTop;
                    setIsScrolled(!!elScrollTop);
                }}
            >
                <Header
                    isHomepage={isHomepage}
                    isScrolled={scrolled}
                    promoBar={promoBar}
                />
                <main
                    ref={mainRef}
                    id="maincontent"
                    onClick={(evt) => {
                        handleClickedEl(evt, scrollRef.current.scrollTop);
                    }}
                    tabIndex={0}
                >
                    {children}
                </main>
                <SiteSidebar excludeSearchBool={excludeSearchBool} />
                <Footer />
            </div>
        </>
    );
}
